/**
 * "Notizen" (Notiz-/Erinnerungsfunktion, Mimmo via Antoine 09.09.2026,
 * docs/KONZEPT-NOTIZEN-ERINNERUNGEN.md) — ersetzt die bisherige, einfache "Aufgaben"-Mini-Karte
 * auf der Veranstaltung-Detail-Seite (dieselbe Datengrundlage: `tasks` gefiltert auf `eventId`,
 * s. Architektur-Entscheidung A im Konzept — kein zweites Datenmodell). Eigenstaendige Karte nach
 * dem Muster von `ZugeordneteNachrichtenKarte.tsx` (eigenes `useApi`, nimmt `eventId` als Prop),
 * gerendert genau an der Stelle, wo vorher die alte Mini-Karte stand.
 *
 * UI/Verhalten sind das freigegebene Artifact-Mockup "Notizen Veranstaltung" (Antoine, 09.09.2026
 * abends) — hier als ECHTE React/TypeScript-Umsetzung mit den Design-System-Bausteinen aus
 * `ui/components.tsx` (`Button`/`Input`/`Card`/`Drawer`/`Select`/`ZeitEingabe`/`StatusPill`),
 * nicht den rohen `btn-*`/`.input`/`.card`/nativen Select-Klassen des Mockups selbst.
 *
 * Die eigentliche Instanz fuer Validierung ist die API (`POST /api/tasks`, `routes/tasks.ts`) —
 * die Regeln hier (Uhrzeit-Pflicht sobald Termin, mind. eine Erinnerung sobald Termin,
 * Obergrenzen je Einheit) sind eine UX-Vorwegnahme derselben Regeln, keine eigene Quelle der
 * Wahrheit. `OFFSET_UNIT_MAX` ist bewusst hier dupliziert (kein gemeinsames Frontend/Backend-Paket
 * fuer so eine kleine Konstante) — Aenderungen an einer Seite ohne die andere fallen sofort auf,
 * weil der Server bei einem Mismatch ohnehin 422 wirft.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useApi, apiMutate } from "../../hooks/useApi";
import type { Member, Task, TaskReminder, TaskReminderOffsetUnit } from "../types";
import { formatDate, formatDateTime } from "../format";
import { Button, Card, DateInput, Drawer, EmptyState, ErrorNote, Icon, Input, LoadingRows, Select, StatusPill, ZeitEingabe, Textarea } from "../components";
import { useContentRefetch } from "../CopilotProvider";

const CARD_TITLE = "font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface";

/** Deckungsgleich mit `OFFSET_UNIT_MAX` in `apps/api/src/lib/notiz-erinnerungen.ts` — s. Kopfkommentar. */
const OFFSET_UNIT_MAX: Record<TaskReminderOffsetUnit, number> = { minutes: 59, hours: 23, days: 30, months: 12 };
const UNIT_LABEL: Record<TaskReminderOffsetUnit, { singular: string; plural: string }> = {
  minutes: { singular: "Minute", plural: "Minuten" },
  hours: { singular: "Stunde", plural: "Stunden" },
  days: { singular: "Tag", plural: "Tage" },
  months: { singular: "Monat", plural: "Monate" },
};
const UNIT_OPTIONS: { value: TaskReminderOffsetUnit; label: string }[] = [
  { value: "minutes", label: "Minuten" },
  { value: "hours", label: "Stunden" },
  { value: "days", label: "Tage" },
  { value: "months", label: "Monate" },
];

function reminderChipLabel(r: TaskReminder): string {
  const l = UNIT_LABEL[r.offsetUnit];
  return `${r.offsetAmount} ${r.offsetAmount === 1 ? l.singular : l.plural} vorher`;
}

/** "<Name> · <Datum, Uhrzeit>" — `createdBy` (Stack-Auth-User-ID) ueber die Team-Mitgliederliste
 *  aufgeloest (`displayName ?? email ?? gekuerzte ID`, dasselbe Fallback-Muster wie `GET
 *  /api/members`s Frontend-Konvention, s. Kommentar dort in `routes/members.ts`). */
function resolveCreatorName(members: Member[], stackUserId: string | null): string {
  if (!stackUserId) return "Unbekannt";
  const m = members.find((x) => x.stackUserId === stackUserId);
  return m?.displayName ?? m?.email ?? `${stackUserId.slice(0, 8)}…`;
}

/** Termin-Chip-Text "10.11.2026, 10:00" — `dueTime` kommt als Postgres-`time` ("HH:MM:SS"). */
function terminText(dueDate: string, dueTime: string | null): string {
  return dueTime ? `${formatDate(dueDate)}, ${dueTime.slice(0, 5)}` : formatDate(dueDate);
}

interface ReminderRow {
  /** Rein lokaler React-`key` fuer die Formular-Zeile, keine Server-ID (die entsteht erst beim
   *  Speichern). */
  localId: string;
  offsetAmount: number;
  offsetUnit: TaskReminderOffsetUnit;
}

let reminderRowSeq = 0;
function neueReminderRow(offsetAmount = 1, offsetUnit: TaskReminderOffsetUnit = "days"): ReminderRow {
  reminderRowSeq += 1;
  return { localId: `r${reminderRowSeq}`, offsetAmount, offsetUnit };
}

const LEER_FORMULAR = () => ({
  text: "",
  dueDate: "",
  dueTime: "",
  // Konzept Entscheidung 4: "Standardmaessig wird nur EINE Erinnerungs-Zeile im Formular
  // vorausgefuellt" — unabhaengig davon, ob schon ein Termin gesetzt ist (Mockup-Vorgabe).
  reminders: [neueReminderRow()] as ReminderRow[],
});

export interface AuftragNotizenKarteProps {
  eventId: string;
  /**
   * Kanban-"⋯"-Menue "Notiz hinzufuegen" (freigegebenes Mockup 11.09.2026,
   * https://claude.ai/code/artifact/d498e616-448e-4432-9247-c8608d335efe) — additive Erweiterung
   * nach dem etablierten `?merge=`-Muster in `customer-detail.tsx`: die Elternseite
   * (`event-detail.tsx`) liest `?notiz=1` und reicht das Ergebnis hier durch, statt dass diese
   * Karte selbst Router-Zugriff braucht. Einmaliges Oeffnen beim Wechsel auf `true` (s.
   * `useEffect` unten) — kein staendiges Wiederoeffnen, falls der Aufrufer den Wert nicht sofort
   * zuruecksetzt.
   */
  autoOpen?: boolean;
}

export function AuftragNotizenKarte({ eventId, autoOpen }: AuftragNotizenKarteProps) {
  const { data, loading, error, reload } = useApi<Task[]>(`/api/tasks?eventId=${eventId}`);
  const membersQ = useApi<Member[]>("/api/members");
  const members = membersQ.data ?? [];
  const notizen = data ?? [];
  // Chat-P1 (uebernommen aus event-detail.tsx, s. Kommentar dort): create/update_task per Chat
  // laedt diese Karte live nach, eigenstaendig abonniert statt ueber die Elternseite.
  useContentRefetch("task", reload);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(LEER_FORMULAR);
  const [zeitBeruehrt, setZeitBeruehrt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [toggleBusyId, setToggleBusyId] = useState<string | null>(null);

  function openDrawer() {
    setForm(LEER_FORMULAR());
    setZeitBeruehrt(false);
    setSubmitError(null);
    setDrawerOpen(true);
  }
  function closeDrawer() {
    setDrawerOpen(false);
  }

  // Kanban-"⋯"-Menue "Notiz hinzufuegen" (s. Prop-Kommentar oben): oeffnet einmalig, wenn
  // `autoOpen` auf `true` wechselt — kein Dauer-Trigger bei jedem Rerender.
  useEffect(() => {
    if (autoOpen) openDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  async function toggleDone(t: Task) {
    setToggleBusyId(t.id);
    try {
      await apiMutate(`/api/tasks/${t.id}`, "PATCH", { status: t.status === "done" ? "open" : "done" });
      reload();
    } finally {
      setToggleBusyId(null);
    }
  }

  // Live-Validierung "Uhrzeit ist Pflicht, sobald ein Termin gesetzt ist" (Mockup-Vorgabe) — erst
  // sichtbar, nachdem der Betrieb ueberhaupt ein Datum ODER eine Uhrzeit angefasst hat (kein
  // Fehler auf dem leeren Formular direkt beim Oeffnen).
  const zeitFehlt = zeitBeruehrt && Boolean(form.dueDate) && !form.dueTime;
  const brauchtErinnerung = Boolean(form.dueDate);
  const erinnerungFehlt = brauchtErinnerung && form.reminders.length === 0;

  function setReminder(localId: string, patch: Partial<ReminderRow>) {
    setForm((f) => ({
      ...f,
      reminders: f.reminders.map((r) => {
        if (r.localId !== localId) return r;
        const next = { ...r, ...patch };
        const max = OFFSET_UNIT_MAX[next.offsetUnit];
        // Mockup-Verhalten (`passeBereichAn`): beim Einheitenwechsel den Wert auf die neue
        // Obergrenze kappen, nie unter 1.
        next.offsetAmount = Math.min(Math.max(next.offsetAmount, 1), max);
        return next;
      }),
    }));
  }
  function removeReminder(localId: string) {
    // Letzte verbleibende Zeile bleibt gesperrt, solange ein Termin gesetzt ist (Konzept
    // Entscheidung 4 — "Alles was ein Datum/Uhrzeit hat, muss eine Erinnerung haben").
    if (form.reminders.length <= 1 && form.dueDate) return;
    setForm((f) => ({ ...f, reminders: f.reminders.filter((r) => r.localId !== localId) }));
  }
  function addReminder() {
    setForm((f) => ({ ...f, reminders: [...f.reminders, neueReminderRow()] }));
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setZeitBeruehrt(true);
    if (!form.text.trim()) return;
    if (form.dueDate && !form.dueTime) return; // Uhrzeit-Pflicht, s. `zeitFehlt` oben
    if (form.dueDate && form.reminders.length === 0) return; // Erinnerung-Pflicht

    setBusy(true);
    setSubmitError(null);
    try {
      await apiMutate("/api/tasks", "POST", {
        eventId,
        title: form.text.trim(),
        dueDate: form.dueDate || null,
        dueTime: form.dueDate ? form.dueTime : null,
        reminders: form.dueDate ? form.reminders.map((r) => ({ offsetAmount: r.offsetAmount, offsetUnit: r.offsetUnit })) : [],
      });
      reload();
      closeDrawer();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className={CARD_TITLE}>Notizen</h3>
          <p className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
            Nebenvereinbarungen, Besichtigungstermine und andere wichtige Infos zu diesem Vorgang
          </p>
        </div>
        <Button type="button" variant="secondary" className="!py-1.5 !text-[12.5px]" onClick={openDrawer}>
          <Icon name="add" className="text-[15px]" /> Notiz hinzufügen
        </Button>
      </div>

      {loading ? (
        <LoadingRows rows={2} />
      ) : error ? (
        <ErrorNote message={error} onRetry={reload} />
      ) : notizen.length === 0 ? (
        /* Vorgangsseite „Salon“: leiser Leerzustand ohne zweiten orangen Knopf — die Aktion steht
           bereits im Kartenkopf (Regel „ein oranger Knopf je Status“). */
        <p className="rounded-lg bg-surface-container-low px-3.5 py-3 font-body-sm text-[12.5px] leading-relaxed text-secondary dark:bg-white/[0.03] dark:text-secondary-fixed-dim">
          <span className="block font-semibold text-on-background dark:text-inverse-on-surface">Noch keine Notizen</span>
          Halte Nebenvereinbarungen, Besichtigungstermine oder andere wichtige Infos zu diesem Vorgang fest.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {notizen.map((t) => {
            const done = t.status === "done";
            return (
              <li key={t.id} className="rounded-xl border border-outline-variant/40 p-3.5 dark:border-outline/30">
                <div className="flex items-start gap-2.5">
                  <button
                    type="button"
                    disabled={toggleBusyId === t.id}
                    onClick={() => void toggleDone(t)}
                    aria-label={done ? "Als offen markieren" : "Als erledigt markieren"}
                    className={`mt-0.5 flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-md border-[1.5px] transition-colors ${
                      done ? "border-outline bg-[#6F675A] text-white" : "border-outline-variant dark:border-outline"
                    }`}
                  >
                    {done && <Icon name="check" className="text-[13px]" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`font-body-sm text-body-sm ${
                        done ? "text-secondary-fixed-dim line-through" : "text-on-background dark:text-inverse-on-surface"
                      }`}
                    >
                      {t.title}
                      {t.notes ? <span className="whitespace-pre-wrap">{t.notes}</span> : null}
                    </p>
                    {(t.dueDate || (t.reminders?.length ?? 0) > 0) && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {t.dueDate && <StatusPill meta={{ label: terminText(t.dueDate, t.dueTime), tone: "info" }} icon="event" />}
                        {t.reminders?.map((r) => (
                          <StatusPill key={r.id} meta={{ label: reminderChipLabel(r), tone: "warn" }} icon="notifications" />
                        ))}
                      </div>
                    )}
                    {t.dueDate && (
                      <p className="mt-1.5 flex items-start gap-1 font-body-sm text-[10.5px] italic text-secondary dark:text-secondary-fixed-dim">
                        <Icon name="calendar_month" className="mt-px shrink-0 text-[13px] text-primary" />
                        So steht es im Kalender am {formatDate(t.dueDate)}: <b className="not-italic font-semibold text-on-surface-variant">Notiz: {t.title}</b>
                      </p>
                    )}
                    <p className="mt-1.5 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                      <b className="font-semibold text-secondary-fixed-dim">{resolveCreatorName(members, t.createdBy)}</b> · {formatDateTime(t.createdAt)}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title="Notiz hinzufügen"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeDrawer}>
              Abbrechen
            </Button>
            <Button type="submit" form="notiz-form" disabled={busy || !form.text.trim()}>
              <Icon name="check" className="text-[16px]" /> {busy ? "Speichert …" : "Notiz speichern"}
            </Button>
          </>
        }
      >
        <form id="notiz-form" onSubmit={(e) => void save(e)} className="space-y-5">
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Notiz</span>
            {/* `Input` rendert ausschliesslich `<input>` (kein `<textarea>`) — Mehrzeiligkeit waere
                verloren, dasselbe bereits etablierte Muster wie in `AngebotSendenDialog.tsx`/
                `GrundNotieren.tsx` (s. `design-komponenten-bestand.test.ts` Kopfkommentar). */}
            <Textarea
              className="min-h-[80px] resize-y"
              placeholder="z. B. Brautpaar wünscht …"
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              maxLength={5_000}
            />
          </label>

          <div>
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Termin <span className="font-normal text-secondary dark:text-secondary-fixed-dim">(optional)</span>
            </span>
            <div className="flex gap-2.5">
              <DateInput
                className="flex-1"
                value={form.dueDate}
                onValueChange={(v) => {
                  setZeitBeruehrt(true);
                  setForm((f) => ({ ...f, dueDate: v }));
                }}
              />
              <ZeitEingabe
                name="dueTime"
                className={`input max-w-[110px] ${zeitFehlt ? "!border-error" : ""}`}
                defaultValue={form.dueTime}
                onValueChange={(v) => {
                  setZeitBeruehrt(true);
                  setForm((f) => ({ ...f, dueTime: v }));
                }}
              />
            </div>
            {zeitFehlt && (
              <p className="mt-1.5 flex items-center gap-1 font-body-sm text-[11px] text-error">
                <Icon name="error" className="text-[13px]" /> Sobald ein Datum gesetzt ist, ist die Uhrzeit Pflicht — sonst lässt sich keine Erinnerung genau berechnen.
              </p>
            )}
            {form.dueDate && (
              <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-surface-container-high px-2.5 py-2 font-body-sm text-[11px] text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
                <Icon name="calendar_month" className="mt-px shrink-0 text-[14px] text-primary" />
                Ein Termin erscheint automatisch im Kalender als <b className="text-on-surface-variant">„Notiz: …"</b> und braucht deshalb mindestens eine Erinnerung.
              </p>
            )}
          </div>

          <div>
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Erinnerungen{" "}
              <span className="font-normal text-secondary dark:text-secondary-fixed-dim">
                (Pflicht, sobald ein Termin gesetzt ist — mindestens eine, beliebig viele weitere)
              </span>
            </span>
            <div className="space-y-2">
              {form.reminders.map((r) => {
                const max = OFFSET_UNIT_MAX[r.offsetUnit];
                const removeDisabled = form.reminders.length <= 1 && Boolean(form.dueDate);
                return (
                  <div key={r.localId} className="flex items-center gap-2 rounded-lg bg-surface-container-high px-2.5 py-2 dark:bg-surface-variant/20">
                    <span className="whitespace-nowrap font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">Erinnere mich</span>
                    <Input
                      type="number"
                      min={1}
                      max={max}
                      value={r.offsetAmount}
                      onChange={(e) => setReminder(r.localId, { offsetAmount: Number(e.target.value) || 1 })}
                      className="!w-16 text-center"
                    />
                    <Select
                      ariaLabel="Erinnerungs-Einheit"
                      value={r.offsetUnit}
                      onValueChange={(v) => setReminder(r.localId, { offsetUnit: v as TaskReminderOffsetUnit })}
                      options={UNIT_OPTIONS}
                    />
                    <span className="whitespace-nowrap font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">vorher</span>
                    <Button
                      type="button"
                      variant="ghost"
                      className="!ml-auto !px-1.5 !py-1"
                      disabled={removeDisabled}
                      title={removeDisabled ? "Mindestens eine Erinnerung ist Pflicht, solange ein Termin gesetzt ist." : "Erinnerung entfernen"}
                      onClick={() => removeReminder(r.localId)}
                    >
                      <Icon name="close" className="text-[15px]" />
                    </Button>
                  </div>
                );
              })}
            </div>
            <Button type="button" variant="ghost" className="mt-2 w-full !border !border-dashed !border-outline-variant" onClick={addReminder}>
              <Icon name="add" className="text-[15px]" /> Weitere Erinnerung hinzufügen
            </Button>
            {erinnerungFehlt && (
              <p className="mt-1.5 flex items-center gap-1 font-body-sm text-[11px] text-error">
                <Icon name="error" className="text-[13px]" /> Mindestens eine Erinnerung ist Pflicht, sobald ein Termin gesetzt ist.
              </p>
            )}
          </div>

          {submitError && <ErrorNote message={submitError} />}
        </form>
      </Drawer>
    </Card>
  );
}
